import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Check } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import { extractFeatures } from '../services/featureExtractionService';
import { generatePRD } from '../services/prdGeneratorService';
import PricingTiersPicker from './PricingTiersPicker';
import { PLAN_ORDER, UPGRADE_PLAN_ORDER } from '../lib/plans';
import { FUNNEL, trackFunnel } from '../lib/funnel';

const ProjectCreationFlow = ({ onClose, onProjectCreated }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { hasAccess, accessTier, hasUsedTrial, paymentStatus, isLoading: paymentLoading, isPaymentRequired, initiatePayment, consumeTrial } = usePayment();
  const PREFILL_KEY = 'project_creation_prefill';

  // Project creation state
  const [projectName, setProjectName] = useState('');
  const [appIdea, setAppIdea] = useState('');
  const [projectType, setProjectType] = useState('Web App');
  const [designStyle, setDesignStyle] = useState('Minimalist');
  const [targetPlatform, setTargetPlatform] = useState('Cursor');
  
  // Payment state
  const [isRestoringProject, setIsRestoringProject] = useState(false);
  
  // Flow state
  const [currentStep, setCurrentStep] = useState(1); // 1: Create, 2: Features, 3: Generate, 4: Complete
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Feature selection state
  const [extractedFeatures, setExtractedFeatures] = useState([]);
  const [suggestedFeatures, setSuggestedFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  
  // Generation state
  const [generatedPRD, setGeneratedPRD] = useState('');
  const [generationError, setGenerationError] = useState(null);
  
  // Created project
  const [createdProject, setCreatedProject] = useState(null);

  // Generating step: rotating status phrases to keep users engaged
  const GENERATION_PHRASES = [
    'Drafting overview…',
    'Selecting core features…',
    'Laying out pages…',
    'Writing technical notes…',
    'Adding example data…',
    'Polishing the document…'
  ];
  const [genPhraseIndex, setGenPhraseIndex] = useState(0);


  // Redirect to auth if not authenticated, with redirect parameter
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=/create-project');
    }
  }, [isAuthenticated, navigate]);

  // Auto-apply prefilled data from Hero and jump to Step 2
  useEffect(() => {
    if (isAuthenticated && currentStep === 1 && !isProcessing) {
      const savedPrefill = localStorage.getItem(PREFILL_KEY);
      if (savedPrefill) {
        try {
          const prefill = JSON.parse(savedPrefill) || {};
          if (prefill.projectName && prefill.appIdea) {
            setProjectName(prefill.projectName);
            setAppIdea(prefill.appIdea);
            setProjectType(prefill.projectType || 'Web App');
            setDesignStyle(prefill.designStyle || 'Minimalist');
            setTargetPlatform(prefill.targetPlatform || 'Cursor');
            const newProject = {
              id: Date.now(),
              name: prefill.projectName.trim(),
              description: prefill.appIdea.trim(),
              type: prefill.projectType || 'Web App',
              createdDate: new Date().toISOString().split('T')[0],
              lastModified: new Date().toISOString().split('T')[0],
              filesCount: 0
            };
            setCreatedProject(newProject);
            // Go straight to feature extraction
            setCurrentStep(2);
            extractFeaturesFromDescription(prefill.appIdea);
          }
        } catch {}
        // Clean up to avoid re-running
        localStorage.removeItem(PREFILL_KEY);
      }
    }
  }, [isAuthenticated, currentStep, isProcessing]);

  // Handle payment success return from Stripe
  useEffect(() => {
    const paymentStatus = searchParams.get('payment') || searchParams.get('sub'); // support legacy subscription param
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      console.log('💳 Payment successful! Session ID:', sessionId);
      setIsRestoringProject(true);

      // Try to restore project from localStorage (but only proceed to dashboard once payment is verified)
      const savedProjectData = localStorage.getItem('project_creation_state');
      if (savedProjectData) {
        try {
          const projectData = JSON.parse(savedProjectData);
          console.log('🔄 Restoring project creation state:', projectData);
          setCreatedProject(projectData);

          // Wait for PaymentContext / server verification to mark user as paid
          const maxWaitMs = 15000;
          const start = Date.now();
          const interval = setInterval(() => {
            const elapsed = Date.now() - start;
            if (!isPaymentRequired) {
              clearInterval(interval);
              localStorage.removeItem('project_creation_state'); // Clean up only after verified
              setTimeout(() => {
                setIsRestoringProject(false);
                onProjectCreated && onProjectCreated(projectData, false);
              }, 800);
            } else if (elapsed > maxWaitMs) {
              clearInterval(interval);
              console.warn('⚠️ Payment still not verified after waiting; staying on paywall.');
              setIsRestoringProject(false);
            }
          }, 1000);

          return () => clearInterval(interval);
        } catch (error) {
          console.error('❌ Failed to restore project state:', error);
          setIsRestoringProject(false);
        }
      } else {
        setIsRestoringProject(false);
      }

      // Clean up URL
      const url = new URL(window.location);
      url.searchParams.delete('payment');
      url.searchParams.delete('sub');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, document.title, url.toString());

    } else if (paymentStatus === 'cancelled') {
      console.log('💳 Payment cancelled by user');
      
      // Try to restore project from localStorage and show paywall again
      const savedProjectData = localStorage.getItem('project_creation_state');
      if (savedProjectData) {
        try {
          const projectData = JSON.parse(savedProjectData);
          console.log('🔄 Restoring project creation state after cancelled payment');
          setCreatedProject(projectData);
          setCurrentStep(4); // Show completion step
          // Don't remove localStorage yet in case they want to retry payment
        } catch (error) {
          console.error('❌ Failed to restore project state:', error);
        }
      }
      
      // Clean up URL
      const url = new URL(window.location);
      url.searchParams.delete('payment');
      url.searchParams.delete('sub');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [searchParams, onProjectCreated, isPaymentRequired]);

  // Monitor payment status changes to proceed after successful payment
  useEffect(() => {
    // Only proceed to dashboard if we're at the completion step (5) and payment is not required
    if (currentStep === 4 && createdProject && isAuthenticated && !isPaymentRequired && generatedPRD) {
      console.log('✅ Payment successful, proceeding to project dashboard...');
      // Add a brief delay to show the completion screen
      const timer = setTimeout(async () => {
        // Legacy: if the user is on the old one-time £1 unlock model, consume it
        // now. The new £1-intro-month subscription does NOT need consuming since
        // it grants unlimited access for the full trial period. consumeTrial()
        // is a no-op for non-legacy users.
        if (accessTier === 'trial' && paymentStatus?.lastPayment?.product_type === 'trial_unlock_once') {
          try {
            await consumeTrial();
            trackFunnel(FUNNEL.TRIAL_CONSUMED);
          } catch (e) { /* non-blocking */ }
        }
        trackFunnel(FUNNEL.DASHBOARD_LANDED, { access_tier: accessTier });
        onProjectCreated && onProjectCreated(createdProject, false); // No guidance needed
      }, 2000); // 2 second delay to show success screen

      return () => clearTimeout(timer);
    }
  }, [currentStep, isPaymentRequired, isAuthenticated, createdProject, generatedPRD, onProjectCreated, accessTier, consumeTrial]);

  // Rotate phrases during Step 4 (Generating Files)
  useEffect(() => {
    if (currentStep === 3 && !isRestoringProject) {
      setGenPhraseIndex(0);
      const intervalId = setInterval(() => {
        setGenPhraseIndex((prev) => (prev + 1) % GENERATION_PHRASES.length);
      }, 1200);
      return () => clearInterval(intervalId);
    }
  }, [currentStep, isRestoringProject]);

  // Don't render anything if not authenticated (after all hooks are called)
  if (!isAuthenticated) {
    return null;
  }

  const projectTypes = [
    'Web App',
    'Mobile App'
  ];

  // Step 1: Create Project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim() || !appIdea.trim()) return;

    setIsProcessing(true);
    
    // Create project
    const newProject = {
      id: Date.now(),
      name: projectName.trim(),
      description: appIdea.trim(),
      type: projectType,
      createdDate: new Date().toISOString().split('T')[0],
      lastModified: new Date().toISOString().split('T')[0],
      filesCount: 0
    };
    
    setCreatedProject(newProject);
    
    // Auto-advance to feature extraction
    setTimeout(() => {
      setIsProcessing(false);
      setCurrentStep(2);
      extractFeaturesFromDescription(appIdea);
    }, 1500);
  };

  // Step 2: Extract Features
  const extractFeaturesFromDescription = async (description) => {
    setIsExtracting(true);
    const startedAt = Date.now();
    trackFunnel(FUNNEL.FEATURES_EXTRACTION_STARTED, { description_length: description?.length || 0 });

    try {
      const result = await extractFeatures(description, true);
      setExtractedFeatures(result.extracted_features || []);
      setSuggestedFeatures(result.suggested_features || []);
      setIsExtracting(false);
      trackFunnel(FUNNEL.FEATURES_EXTRACTION_COMPLETED, {
        duration_ms: Date.now() - startedAt,
        extracted_count: (result.extracted_features || []).length,
        suggested_count: (result.suggested_features || []).length,
      });
    } catch (error) {
      console.error('Feature extraction failed:', error);
      // Fallback to basic extraction
      setExtractedFeatures(['User Authentication', 'Database Integration', 'API Integration']);
      setSuggestedFeatures(['Mobile Responsive', 'Search Functionality', 'Notifications', 'Analytics Dashboard']);
      setIsExtracting(false);
      trackFunnel(FUNNEL.FEATURES_EXTRACTION_COMPLETED, {
        duration_ms: Date.now() - startedAt,
        fallback: true,
      });
    }
  };

  // Step 3: Handle Feature Selection — skip design, go straight to generation
  const handleFeatureSelection = (features) => {
    setSelectedFeatures(features);
    trackFunnel(FUNNEL.FEATURES_SELECTED, { selected_count: features?.length || 0 });
    setCurrentStep(3); // Move to generation
    generateFiles(features);
  };

  // Generate PRD files
  const generateFiles = async (featuresOverride = null) => {
    setIsProcessing(true);
    setGenerationError(null);

    // Use design style from hero prefill or default
    const designToUse = { id: null, name: designStyle || 'Minimalist' };
    const featuresToUse = featuresOverride || selectedFeatures;
    const generationStartedAt = Date.now();
    trackFunnel(FUNNEL.PRD_GENERATION_STARTED, {
      target_platform: targetPlatform,
      design_style: designStyle,
      project_type: projectType,
      features_count: featuresToUse?.length || 0,
    });

    try {
      console.log('🚀 Starting file generation...');
      console.log('📋 Parameters for generation:');
      console.log('  - App Idea length:', appIdea?.length);
      console.log('  - Selected Features:', selectedFeatures);
      console.log('  - Design to use:', designToUse);
      console.log('  - Design ID:', designToUse?.id);
      console.log('  - Design Name:', designToUse?.name);
      
      // Generate PRD
      console.log('📝 Calling generatePRD...');
      const prd = await generatePRD(appIdea, featuresToUse, designToUse, (partial) => {
        // Update progressively so the UI can reflect progress if desired
        setGeneratedPRD(partial);
      }, targetPlatform);

      console.log('✅ PRD generated successfully');
      console.log('📊 PRD length:', prd?.length);

      setGeneratedPRD(prd);
      
      // Update project with file count, features, design, and generated content
      const updatedProject = {
        ...createdProject,
        features: featuresToUse,
        selectedFeatures: featuresToUse,
        filesCount: 1,
        lastModified: new Date().toISOString().split('T')[0],
        generatedFiles: [
          {
            id: 'prd-' + Date.now(),
            name: `${createdProject.name.replace(/\s+/g, '-')}-prd.md`,
            type: 'PRD Document',
            content: prd,
            date: new Date().toISOString().split('T')[0],
            size: `${Math.round(prd.length / 1024)}KB`,
            icon: 'FileText'
          }
        ]
      };
      setCreatedProject(updatedProject);
      
      console.log('Project with generated files:', updatedProject); // Debug log
      
      setCurrentStep(4); // Complete
      setCreatedProject(updatedProject);
      
      trackFunnel(FUNNEL.PRD_GENERATION_COMPLETED, {
        duration_ms: Date.now() - generationStartedAt,
        prd_length: prd?.length || 0,
        target_platform: targetPlatform,
      });

      // Check if payment is required after project generation
      if (isAuthenticated && isPaymentRequired) {
        console.log('🔒 Payment required, showing paywall...');
        trackFunnel(FUNNEL.PAYWALL_SHOWN, {
          context: 'project-creation',
          has_used_trial: !!hasUsedTrial,
          plans_offered: hasUsedTrial ? UPGRADE_PLAN_ORDER : PLAN_ORDER,
        });

        // Save project state before showing paywall
        localStorage.setItem('project_creation_state', JSON.stringify(updatedProject));
        console.log('💾 Saved project state for payment flow');

        // Continue to step 5 to show paywall inline
        return;
      }

      // For paid users, the useEffect will handle the transition to dashboard
      // after showing the completion screen briefly
      console.log('✅ Project generated for paid user, showing completion screen...');
    } catch (error) {
      setGenerationError(error.message);
      trackFunnel(FUNNEL.PRD_GENERATION_FAILED, {
        duration_ms: Date.now() - generationStartedAt,
        message: error?.message || 'unknown',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };


  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full px-6 py-6 fixed top-0 left-0 right-0 z-50 backdrop-blur-sm"
      >
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="bg-gray-100/80 backdrop-blur-sm rounded-full px-6 py-3 flex items-center space-x-4 shadow-sm border border-white/20">
            <h1 className="text-xl font-bold text-black">Valyfy</h1>
            <span className="text-black/40">|</span>
            <span className="text-sm text-black/60">
              {currentStep === 1 ? 'Create Project' :
               currentStep === 2 ? 'Select Features' :
               currentStep === 3 ? 'Generating Files' :
               'Project Ready'}
            </span>
          </div>
          {/* Hide close button on paywall/timer screen (step 5 when payment is required) */}
          {(currentStep !== 4 || !isPaymentRequired) && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="p-3 hover:bg-black/5 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-black/60" />
            </motion.button>
          )}
        </div>
      </motion.header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 pt-24 sm:pt-32 pb-12 flex flex-col min-h-screen">
        
        {/* Payment Success & Project Restoration */}
        {isRestoringProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-grow flex flex-col items-center justify-center text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <Check className="w-10 h-10 text-white" />
            </motion.div>

            <h1 className="font-jersey text-4xl md:text-5xl text-black leading-tight mb-4">
              Payment Successful! 🎉
            </h1>
            <p className="text-black/60 text-lg mb-8">
              Restoring your project and setting up your dashboard...
            </p>
            
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-2 border-vibe-cyan border-t-transparent rounded-full"
            />
          </motion.div>
        )}
        {/* Step 1: Create Project */}
        {!isRestoringProject && currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center py-16"
          >
            <h1 className="font-jersey text-4xl md:text-5xl text-black leading-tight mb-4">
              Create new project
            </h1>
            <p className="text-black/60 text-lg mb-12">
              Start by describing your amazing app idea ✨
            </p>

            {!isProcessing ? (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                onSubmit={handleCreateProject}
                className="bg-white/60 backdrop-blur-xl border border-black/10 rounded-2xl p-8 shadow-lg max-w-2xl mx-auto"
              >
                <div className="space-y-6">
                  {/* Project Name */}
                  <div className="text-left">
                    <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                      Project Name
                    </label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="e.g., E-commerce Platform"
                      className="w-full px-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent text-lg bg-white/50 backdrop-blur-sm placeholder-black/40"
                      required
                    />
                  </div>

                  {/* Project Type */}
                  <div className="text-left">
                    <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                      Project Type
                    </label>
                    <select
                      value={projectType}
                      onChange={(e) => setProjectType(e.target.value)}
                      className="w-full px-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent text-lg bg-white/50 backdrop-blur-sm"
                    >
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* App Idea */}
                  <div className="text-left">
                    <label className="block text-sm font-semibold text-black/60 uppercase tracking-wider mb-3">
                      Describe Your App Idea
                    </label>
                    <textarea
                      value={appIdea}
                      onChange={(e) => setAppIdea(e.target.value)}
                      placeholder="Describe your app idea in detail. What does it do? Who is it for? What are the main features? The more detail you provide, the better files we can generate."
                      rows={6}
                      className="w-full px-4 py-4 border border-black/10 rounded-xl focus:ring-2 focus:ring-vibe-cyan focus:border-transparent resize-none bg-white/50 backdrop-blur-sm placeholder-black/40"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center space-x-4 pt-6">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={onClose}
                      className="px-8 py-3 border border-black/10 rounded-xl font-medium text-black/70 hover:bg-black/5 transition-colors"
                    >
                      Cancel
                    </motion.button>
                    
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={!projectName.trim() || !appIdea.trim()}
                      className="px-8 py-3 bg-vibe-cyan text-black rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 transition-all flex items-center space-x-2"
                    >
                      <span>Continue</span>
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.form>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="flex justify-center mb-8">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 border-2 border-vibe-cyan border-t-transparent rounded-full"
                  />
                </div>
                <h2 className="text-2xl font-light text-black mb-4">
                  Creating your project...
                </h2>
                <p className="text-black/60">
                  Setting up workspace and preparing feature extraction ✨
                </p>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Step 2: Feature Extraction */}
        {currentStep === 2 && isExtracting && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="w-full max-w-4xl text-center flex-grow flex flex-col items-center justify-center"
          >
            <div className="mb-8">
              <h1 className="font-jersey text-4xl md:text-5xl text-black leading-tight mb-4">
                Analyzing {createdProject?.name}
              </h1>
              <p className="text-black/60 text-lg mb-4">
                Extracting features from your project description ✨
              </p>
              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 max-w-2xl mx-auto mb-8">
                <p className="text-sm text-black/60 italic">
                  "{createdProject?.description}"
                </p>
              </div>
            </div>

            <div className="flex justify-center mb-8">
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="w-20 h-20 border border-black/10 rounded-full"
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 w-20 h-20 border-t border-vibe-cyan rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-2 w-16 h-16 bg-white/60 backdrop-blur-xl rounded-full border border-black/5"
                  style={{ 
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                />
              </div>
            </div>
            
            <div className="space-y-4 max-w-md mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center text-sm text-black/60"
              >
                <div className="w-1.5 h-1.5 bg-vibe-cyan rounded-full mr-3"></div>
                Analyzing app description
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 }}
                className="flex items-center text-sm text-black/60"
              >
                <div className="w-1.5 h-1.5 bg-vibe-cyan rounded-full mr-3"></div>
                Extracting core features
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5 }}
                className="flex items-center text-sm text-black/60"
              >
                <div className="w-1.5 h-1.5 bg-black rounded-full mr-3"></div>
                Preparing feature selection
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Feature Selection */}
        {!isRestoringProject && currentStep === 2 && !isExtracting && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="w-full max-w-6xl h-full flex flex-col"
          >
            <div className="text-center mb-8">
              <h1 className="text-4xl font-light text-black mb-2 tracking-tight">
                Select features
              </h1>
              <p className="text-black/60 text-lg">
                Choose what to include in your {createdProject?.name}
              </p>
            </div>

            <div className="flex-1 min-h-0">
              <FeatureSelector
                extractedFeatures={extractedFeatures}
                suggestedFeatures={suggestedFeatures}
                onSelectionComplete={handleFeatureSelection}
                onBack={handleBack}
              />
            </div>
          </motion.div>
        )}

        {/* Step 3: Generating Files — live stream */}
        {!isRestoringProject && currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-grow flex flex-col items-center"
          >
            <div className="w-full max-w-3xl mx-auto px-3 sm:px-4 pt-2 sm:pt-4">
              <div className="text-center mb-4 sm:mb-6">
                <div className="inline-flex items-center gap-2 mb-2 sm:mb-3">
                  <motion.span
                    animate={{ scale: [1, 1.4, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-vibe-cyan"
                  />
                  <span className="text-xs sm:text-sm font-medium text-slate-600 uppercase tracking-wider">
                    Streaming live…
                  </span>
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 mb-2 px-2">
                  Writing your PRD in real time
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 px-3">
                  Watch it appear below as the AI drafts each section.
                </p>
              </div>

              <div className="relative bg-white border border-black/10 rounded-2xl shadow-lg overflow-hidden">
                <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-black/5 bg-gray-50/50 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-slate-500 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
                    <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                    <span className="ml-2 sm:ml-3 font-mono truncate">{createdProject?.name?.replace(/\s+/g, '-') || 'project'}-prd.md</span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-slate-400 font-mono whitespace-nowrap flex-shrink-0">
                    {generatedPRD ? `${generatedPRD.length.toLocaleString()} chars` : 'starting…'}
                  </span>
                </div>
                <div
                  ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
                  className="px-3 sm:px-6 py-4 sm:py-5 max-h-[55vh] overflow-y-auto"
                >
                  {generatedPRD ? (
                    <pre className="text-[12px] sm:text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words font-mono">
                      {generatedPRD}
                      <span className="inline-block w-1.5 h-3.5 bg-slate-700 ml-0.5 align-middle animate-pulse" />
                    </pre>
                  ) : (
                    <div className="flex items-center gap-3 text-sm text-slate-400 py-4">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                        className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm">{GENERATION_PHRASES[genPhraseIndex]}</span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-center text-[11px] sm:text-xs text-slate-400 mt-3 sm:mt-4 px-3">
                Don't refresh — we're streaming directly from the model.
              </p>
            </div>
          </motion.div>
        )}

        {/* Step 4: Complete */}
        {!isRestoringProject && currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-vibe-cyan rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <Check className="w-10 h-10 text-black" />
            </motion.div>

            <h1 className="font-jersey text-4xl md:text-5xl text-black leading-tight mb-4">
              Your Optimized AI Prompt is Ready! ✨
            </h1>
            <p className="text-black/60 text-lg mb-8">
              We've generated a complete PRD prompt for {createdProject?.name}
            </p>
            
            {isAuthenticated && isPaymentRequired ? (
              <div className="w-full max-w-6xl mx-auto mb-8 sm:mb-12 px-3 sm:px-4">
                <div className="relative rounded-2xl overflow-hidden border border-black/10 shadow-xl">
                  {/* Background preview - blurred to tease content */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 md:p-12 pointer-events-none">
                    <div className="prose prose-sm max-w-none text-left">
                      <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-xs sm:text-sm">
                        {generatedPRD?.substring(0, 600)}...
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/85 to-white/95 backdrop-blur-md pointer-events-none"></div>

                  {/* Pricing picker on top */}
                  <div className="relative z-10 px-3 sm:px-8 py-6 sm:py-10">
                    <div className="text-center mb-5 sm:mb-8 max-w-2xl mx-auto">
                      <div className="inline-flex w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-vibe-cyan to-blue-400 rounded-full items-center justify-center mb-3 sm:mb-4">
                        <span className="text-xl sm:text-2xl">📄</span>
                      </div>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-black mb-2 px-2">
                        {hasUsedTrial ? 'Pick a plan to keep building' : 'Choose how you want to use Valycode'}
                      </h3>
                      <p className="text-sm sm:text-base text-black/60 px-2">
                        {hasUsedTrial
                          ? "You've already used your £1 intro. Pick Pro monthly or Lifetime to keep generating."
                          : 'Start for £1 your first month, go monthly, or pay once for life.'}
                      </p>
                    </div>
                    <PricingTiersPicker
                      plans={hasUsedTrial ? UPGRADE_PLAN_ORDER : PLAN_ORDER}
                      context="project-creation"
                      onSelect={async (planId) => {
                        try {
                          await initiatePayment(planId, 'project-creation');
                        } catch (error) {
                          console.error('Payment failed:', error);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="bg-vibe-cyan/20 border border-vibe-cyan/30 rounded-xl p-4 mb-12 max-w-md mx-auto"
              >
                <p className="text-sm text-black/70">
                  ✨ <strong>Setup guide coming up!</strong> We'll show you how to configure everything in just a moment...
                </p>
              </motion.div>
            )}

            {!isPaymentRequired && (
              <div className="flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    // Legacy one-time £1 unlock only — new subscription trial has no consume step.
                    if (accessTier === 'trial' && paymentStatus?.lastPayment?.product_type === 'trial_unlock_once') {
                      try { await consumeTrial(); } catch (e) { /* non-blocking */ }
                    }
                    onClose && onClose();
                  }}
                  className="px-8 py-3 bg-slate-800 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Go to Dashboard
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </main>



    </div>
  );
};

// Feature Selector Component
const FeatureSelector = ({ extractedFeatures, suggestedFeatures, onSelectionComplete, onBack }) => {
  const [selectedFeatures, setSelectedFeatures] = useState([...extractedFeatures]);

  const toggleFeature = (featureName) => {
    setSelectedFeatures(prev => 
      prev.includes(featureName) 
        ? prev.filter(f => f !== featureName)
        : [...prev, featureName]
    );
  };

  const handleComplete = () => {
    onSelectionComplete(selectedFeatures);
  };

  return (
    <div className="space-y-8">
      {/* Extracted Features */}
      {extractedFeatures.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-black mb-4">Detected from your description:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {extractedFeatures.map(feature => (
              <motion.button
                key={feature}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => toggleFeature(feature)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  selectedFeatures.includes(feature)
                    ? 'bg-vibe-cyan/20 border-vibe-cyan text-black'
                    : 'bg-white border-black/10 hover:border-black/20 text-black/70'
                }`}
              >
                <div className="text-sm font-medium">{feature}</div>
                <div className="text-xs mt-1 opacity-60">Auto-detected</div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Features */}
      {suggestedFeatures.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-black mb-4">Additional features you might need:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {suggestedFeatures.map(feature => (
              <motion.button
                key={feature}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => toggleFeature(feature)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  selectedFeatures.includes(feature)
                    ? 'bg-black/10 border-black/20 text-black'
                    : 'bg-white border-black/5 hover:border-black/10 text-black/50'
                }`}
              >
                <div className="text-sm font-medium">{feature}</div>
                <div className="text-xs mt-1 opacity-40">Optional</div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-8">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-8 py-3 rounded-full border border-black/20 text-black/60 hover:bg-black/5 transition-all"
        >
          Back
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleComplete}
          disabled={selectedFeatures.length === 0}
          className="px-8 py-3 rounded-full bg-vibe-cyan text-black font-semibold hover:shadow-lg disabled:opacity-50 transition-all"
        >
          Continue with {selectedFeatures.length} features
        </motion.button>
      </div>
    </div>
  );
};

export default ProjectCreationFlow; 